import { createRouter, createWebHistory } from 'vue-router';
import processPayload from './middlewares/processPayload'; // eslint-disable-line import/no-cycle
import fetchSuccessPageData from './middlewares/fetchSuccessPageData';
import redirectToSuccessPageIfAlreadySent from './middlewares/redirectToSuccessPageIfAlreadySent';
import track from './middlewares/track';
import reloadParentIfIFrame from './middlewares/reloadIfIFrame';
import trackLoginWallInteractions from './middlewares/trackLoginWallInteractions';
import { defaultRegisterStateBuilder } from './utils/stateBuilder';
import {
    isLoggedInUser,
} from './middlewares/auth';
import {
    backendRoute,
} from './utils/route';
import { redirect } from './utils/navigation';

import PageSending from './components/pages/PageSending.vue';
import PageSuccess from './components/pages/PageSuccess.vue';
import PageForm from './components/pages/PageForm.vue';
import {
    CREATE_CV,
    PAGE_CUSTOM_FIELDS,
    PAGE_DEBUG,
    PAGE_ERROR,
    PAGE_ERROR_ALREADY_SENT,
    PAGE_FORM,
    PAGE_JOBS,
    PAGE_PREVIEW,
    PAGE_SENDING,
    PAGE_SUCCESS,
} from './routes';
import { useApplicationStore } from './stores/application-store';
import { useProfile } from './stores/profile-store';
import { usePayloadStore } from './stores/payload-store';
import { useJobDataStore } from './stores/job-store';
import { useOverlays } from './stores/overlays-store';
import { devOnly } from './utils/devOnly';

const PageError = () => import('./components/pages/PageError.vue');
const PageErrorAlreadySent = () => import('./components/pages/PageErrorAlreadySent.vue');

export function getRelativeRedirectUrl(to, from) {
    return to.query?.redirectUrl || to.redirectedFrom?.fullPath || from.fullPath;
}

export function handleOAuthRedirect(to, from) {
    const url = new URL(to.fullPath, window.location.origin);
    const absoluteRedirectUrl = new URL(getRelativeRedirectUrl(to, from), window.location.origin);
    absoluteRedirectUrl.searchParams.set('oauthAction', to.name);
    const payload = usePayloadStore();
    url.searchParams.set('redirectUrl', absoluteRedirectUrl.href);
    url.searchParams.set('uuid', payload.atsUserUuid);
    url.searchParams.set('payload', payload.encryptedPayload);

    if (to.query.registerState) {
        url.searchParams.set('registerState', to.query.registerState);
        url.searchParams.set('state', to.query.registerState);
    } else {
        const state = defaultRegisterStateBuilder(payload.atsUserUuid, payload.encryptedPayload);
        url.searchParams.set('registerState', state);
        url.searchParams.set('state', state);
    }

    redirect(url.href);
    return false;
}

const router = createRouter({
    history: createWebHistory(),
    scrollBehavior: () => ({ left: 0, top: 0 }),
    routes: [
        {
            path: '/bewerben',
            name: PAGE_FORM,
            component: PageForm,
            meta: {
                middleware: [
                    reloadParentIfIFrame,
                    trackLoginWallInteractions,
                    redirectToSuccessPageIfAlreadySent,
                    processPayload,
                    fetchSuccessPageData,
                ],
            },
        },
        {
            path: '/bewerben/vorschau',
            name: PAGE_PREVIEW,
            component: PageForm,
            props: {
                previewMode: true,
            },
            beforeEnter: (to, from, next) => {
                if (from.name !== PAGE_FORM && from.name !== PAGE_CUSTOM_FIELDS) {
                    next({
                        name: PAGE_FORM,
                    });
                    return;
                }
                next();
            },
        },
        {
            path: '/bewerben/zusaetzliche-infos',
            name: PAGE_CUSTOM_FIELDS,
            component: PageForm,
            props: {
                customFieldsMode: true,
            },
            beforeEnter: (to, from, next) => {
                if (from.name !== PAGE_FORM && from.name !== PAGE_PREVIEW) {
                    next({
                        name: PAGE_FORM,
                    });
                    return;
                }
                next();
            },
        },
        {
            path: '/bewerben/versenden',
            name: PAGE_SENDING,
            component: PageSending,
            beforeEnter: (to, from, next) => {
                if (from.name !== PAGE_FORM && from.name !== PAGE_PREVIEW && from.name !== PAGE_CUSTOM_FIELDS) {
                    next({
                        name: PAGE_ERROR_ALREADY_SENT,
                    });
                    return;
                }
                next();
            },
        },
        {
            path: '/bewerben/erfolgreich',
            name: PAGE_SUCCESS,
            component: PageSuccess,
            beforeEnter: (to, from, next) => {
                const application = useApplicationStore();
                if (application.uuid) {
                    next();
                } else {
                    // Catching empty error because of
                    // https://github.com/vuejs/vue-router/issues/2872#issuecomment-519073998.
                    router.push({
                        name: PAGE_ERROR,
                    }).catch(() => {});
                }
            },
        },
        {
            path: backendRoute('backend.authentication.oauthRegister'),
            name: 'register',
            beforeEnter: handleOAuthRedirect,
        },
        {
            path: backendRoute('backend.authentication.oauthLogin'),
            name: 'login',
            beforeEnter: handleOAuthRedirect,
        },
        {
            path: backendRoute('backend.authentication.oauthLogout'),
            name: 'logout',
            beforeEnter: (to, from) => {
                const application = useApplicationStore();
                application.$reset();

                const profile = useProfile();
                profile.$reset();

                return handleOAuthRedirect(to, from);
            },
        },
        {
            path: '/profil/anlegen?application=1',
            name: CREATE_CV,
            beforeEnter: (to) => {
                redirect(to.fullPath);
            },
        },
        {
            path: '/jobs',
            name: PAGE_JOBS,
            beforeEnter: (to) => {
                redirect(to.fullPath);
            },
        },
        {
            path: '/profil/bewerbungen',
            name: 'bewerbungen',
            beforeEnter: (to) => {
                redirect(to.fullPath);
            },
        },
        {
            path: '/bewerben/bereits-gesendet',
            name: PAGE_ERROR_ALREADY_SENT,
            component: PageErrorAlreadySent,
        },
        {
            path: '/:pathMatch(.*)*',
            name: PAGE_ERROR,
            component: PageError,
        },
    ],
});

// Prevent debug page to be available on production.
devOnly(() => {
    router.addRoute({
        path: '/bewerben/debug',
        name: PAGE_DEBUG,
        component: () => import('./components/pages/PageDebug.vue'),
        beforeEnter: () => {
            const application = useApplicationStore();
            const payload = usePayloadStore();
            const job = useJobDataStore();
            const profile = useProfile();

            application.$reset();
            payload.$reset();
            job.$reset();
            profile.$reset();
        },
    });
});

const defaultMiddleware = [track];

router.beforeEach(async (to, from) => {
    let middleware = [];
    if (to.meta.middleware) {
        middleware = Array.isArray(to.meta.middleware) ? to.meta.middleware : [to.meta.middleware];
    }
    middleware = middleware.concat(defaultMiddleware);

    // Always check if user is logged in
    middleware.unshift(isLoggedInUser);

    const context = {
        from,
        router,
        to,
    };

    // Loop through middleware, return result if navigation failed middleware validation
    // eslint-disable-next-line no-restricted-syntax
    for (const nextMiddlewareInLine of middleware) {
        // eslint-disable-next-line no-await-in-loop
        const middlewareResult = await nextMiddlewareInLine(context);
        if (middlewareResult === false || typeof middlewareResult === 'object') return middlewareResult;
    }
    return true;
});

router.afterEach(() => {
    const overlays = useOverlays();
    overlays.initialLoad = false;
});

export default router;
